import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Chart, registerables, ChartConfiguration, ChartData } from 'chart.js';
import { environment } from '../../../../environments/environment';

// Chart.js v3 is tree-shakeable; register controllers/elements/scales so the
// line/bar/doughnut charts actually render.
Chart.register(...registerables);

interface RangeOption { key: string; label: string; }

@Component({
  selector: 'app-default',
  templateUrl: './default.component.html',
  styleUrls: ['./default.component.scss']
})
export class DefaultComponent implements OnInit {

  loading = false;
  activeRange = '7d';
  rangeLabel = '';

  ranges: RangeOption[] = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d', label: 'Last 7 Days' },
    { key: '30d', label: 'Last 30 Days' },
    { key: 'month', label: 'This Month' },
    { key: '6m', label: 'Last 6 Months' },
    { key: '1y', label: 'Last 1 Year' },
  ];

  // Summary cards
  newCustomers = 0;
  totalScans = 0;
  dealerScans = 0;
  normalScans = 0;
  payoutTotal = 0;
  payoutPaid = 0;
  payoutPending = 0;

  // Marketing (Ankhan only; hidden if the endpoint isn't available)
  hasMarketing = false;
  spendCurrency = 'INR';
  spendTotal = 0;
  msgDelivered = 0;
  msgRead = 0;

  // ---- Charts ----
  scansLine: ChartData<'line'> = { labels: [], datasets: [] };
  customersLine: ChartData<'line'> = { labels: [], datasets: [] };
  statusDoughnut: ChartData<'doughnut'> = { labels: [], datasets: [] };
  typeDoughnut: ChartData<'doughnut'> = { labels: [], datasets: [] };
  topProductsBar: ChartData<'bar'> = { labels: [], datasets: [] };

  lineOptions: ChartConfiguration['options'] = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: true, position: 'bottom' } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };
  barOptions: ChartConfiguration['options'] = {
    responsive: true, maintainAspectRatio: false, indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
  };
  doughnutOptions: ChartConfiguration['options'] = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
  };

  constructor(private http: HttpClient, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.setRange('7d');
  }

  setRange(key: string): void {
    this.activeRange = key;
    const { from, to } = this.computeRange(key);
    this.rangeLabel = `${this.fmt(from)} → ${this.fmt(to)}`;
    this.load(from, to);
  }

  private computeRange(key: string): { from: Date; to: Date } {
    const to = new Date();
    const from = new Date();
    switch (key) {
      case 'today': break;
      case 'yesterday': from.setDate(from.getDate() - 1); to.setDate(to.getDate() - 1); break;
      case '7d': from.setDate(from.getDate() - 6); break;
      case '30d': from.setDate(from.getDate() - 29); break;
      case 'month': from.setDate(1); break;
      case '6m': from.setMonth(from.getMonth() - 6); break;
      case '1y': from.setFullYear(from.getFullYear() - 1); break;
    }
    return { from, to };
  }

  private fmt(d: Date): string {
    const p = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  load(from: Date, to: Date): void {
    this.loading = true;
    const qs = `?fromDate=${this.fmt(from)}&toDate=${this.fmt(to)}`;

    this.http.get<any>(`${environment.serverUrl}/dashboard/summary${qs}`).subscribe({
      next: (res) => { this.applySummary(res); this.loading = false; },
      error: (e) => {
        this.loading = false;
        console.error('dashboard summary failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to load dashboard', 'Error');
      }
    });

    // Marketing spend — optional. Hidden if the endpoint 404s (e.g. v1.0 has no campaigns).
    this.http.get<any>(`${environment.serverUrl}/marketing/analytics/spend${qs}`).subscribe({
      next: (s) => {
        this.hasMarketing = true;
        this.spendCurrency = s?.currency || 'INR';
        this.spendTotal = s?.totals?.estimatedCost || 0;
        this.msgDelivered = s?.totals?.delivered || 0;
        this.msgRead = s?.totals?.read || 0;
      },
      error: () => { this.hasMarketing = false; }
    });
  }

  private applySummary(res: any): void {
    this.newCustomers = res?.customers?.total || 0;
    this.totalScans = res?.scans?.total || 0;
    this.dealerScans = res?.scans?.byType?.dealer?.count || 0;
    this.normalScans = res?.scans?.byType?.normal?.count || 0;
    this.payoutTotal = res?.payout?.total || 0;
    this.payoutPaid = res?.payout?.paid || 0;
    this.payoutPending = res?.payout?.pending || 0;

    const scanSeries = res?.scans?.series || [];
    this.scansLine = {
      labels: scanSeries.map((r: any) => r.date),
      datasets: [
        { label: 'Dealer scans', data: scanSeries.map((r: any) => r.dealer), borderColor: '#e5507e', backgroundColor: 'rgba(229,80,126,0.15)', fill: true, tension: 0.3 },
        { label: 'Normal scans', data: scanSeries.map((r: any) => r.normal), borderColor: '#3b7ddd', backgroundColor: 'rgba(59,125,221,0.15)', fill: true, tension: 0.3 },
      ],
    };

    const custSeries = res?.customers?.series || [];
    this.customersLine = {
      labels: custSeries.map((r: any) => r.date),
      datasets: [
        { label: 'New customers', data: custSeries.map((r: any) => r.count), borderColor: '#4bbf73', backgroundColor: 'rgba(75,191,115,0.15)', fill: true, tension: 0.3 },
      ],
    };

    const status = res?.scans?.byStatus || { Open: 0, Paid: 0, SentToBank: 0 };
    this.statusDoughnut = {
      labels: ['Unpaid', 'Paid', 'Sent to Bank'],
      datasets: [{ data: [status.Open, status.Paid, status.SentToBank], backgroundColor: ['#f0ad4e', '#4bbf73', '#3b7ddd'] }],
    };

    this.typeDoughnut = {
      labels: ['Dealer scans', 'Normal scans'],
      datasets: [{ data: [this.dealerScans, this.normalScans], backgroundColor: ['#e5507e', '#3b7ddd'] }],
    };

    const top = res?.topProducts || [];
    this.topProductsBar = {
      labels: top.map((r: any) => r.name),
      datasets: [{ label: 'Scans', data: top.map((r: any) => r.count), backgroundColor: '#8e5bd8' }],
    };
  }

  refresh(): void {
    this.setRange(this.activeRange);
  }
}
